import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { paymentAPI } from '../services/api';
import { CheckCircle, Loader, XCircle } from 'lucide-react';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('failed');
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const response = await paymentAPI.getCheckoutStatus(sessionId);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('failed');
      } else {
        // Still processing, poll again
        setAttempts(prev => prev + 1);
        setTimeout(checkPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(checkPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Card className="glass-effect p-12 max-w-lg text-center">
        {status === 'checking' && (
          <>
            <Loader className="w-16 h-16 mx-auto mb-6 text-blue-600 animate-spin" />
            <h1 className="text-3xl font-bold mb-4">Processing Your Subscription</h1>
            <p className="text-gray-600">
              Please wait while we confirm your payment...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 mx-auto mb-6 text-green-600" />
            <h1 className="text-3xl font-bold mb-4">Subscription Activated!</h1>
            <p className="text-gray-600 mb-8">
              Thank you for subscribing to Pressed. You now have access to all Core member features.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600"
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
            </Button>
          </>
        )}

        {status === 'failed' && (
          <>
            <XCircle className="w-16 h-16 mx-auto mb-6 text-red-600" />
            <h1 className="text-3xl font-bold mb-4">Payment Processing Issue</h1>
            <p className="text-gray-600 mb-8">
              There was an issue processing your payment. Please try again or contact support.
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => navigate('/subscription')}
                variant="outline"
                data-testid="try-again-btn"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard-btn"
              >
                Back to Dashboard
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;